import { setMoneyLocale } from "@gladis/shared";
import { useSyncExternalStore } from "react";

/**
 * Which language the reader asked for, which one that resolves to, and how their figures are written.
 *
 * ==========================================================================================
 * THIS IS `lib/theme.ts` ONE PREFERENCE ALONG, AND THE RESEMBLANCE IS DELIBERATE
 * ==========================================================================================
 *
 * Every sub-problem here is one that module already solved, and solved for the same reasons: a
 * preference that belongs to a BROWSER rather than to an account, storage that raises rather than
 * returning null when site data is blocked, a snapshot for a render with no `window`, and a third
 * state meaning "follow the environment". Copying its shape means this module's failure modes are
 * ones the repository has already met, argued about, and tested.
 *
 * Where it differs, it differs for a stated reason. `theme.ts` watches a `MediaQueryList`; this
 * watches the `languagechange` event, which is the same idea — the environment changing its own
 * answer under a `system` choice — with none of `matchMedia`'s mint-a-new-object trap.
 *
 * ==========================================================================================
 * `locale` IS DERIVED FROM `language`, AND THAT IS THE WHOLE FEATURE'S CENTRAL INVARIANT
 * ==========================================================================================
 *
 * The roadmap's sharpest trap for this feature is Dutch words rendered beside `€1,234.50`. It exists
 * because `formatEur` and the interface would otherwise read the same source — `navigator.language` —
 * and an explicit choice that wins permanently is exactly what makes the two disagree.
 *
 * So the formatting locale is not read from the browser at all. It is a total function of the
 * resolved language, computed here and nowhere else. That state is not merely tested for; it cannot
 * be constructed. The E2E assertion that covers it is a regression guard on the wiring, not the thing
 * holding the invariant up.
 *
 * A REGION DECIDES NOTHING. `en-US`, `en-AU` and `en-GB` all resolve to English and are all formatted
 * `en-GB`. That is a deliberate narrowing — see `apps/app/src/main.tsx`, whose comment about a member
 * in Boston this decision falsified and which was corrected rather than left to disagree.
 */

/** The two languages the interface offers. Not a BCP-47 tag: a tag carries a region, and a region decides nothing. */
export type Language = "nl" | "en";

/** What a browser has settled on. `system` is the default and means "follow this browser". */
export type LanguageChoice = Language | "system";

/** The formatting conventions, one per language. The only two values `locale` ever takes. */
export type LanguageLocale = "nl-NL" | "en-GB";

export interface ResolvedLanguage {
  /** Which catalogue to read and which words to render. */
  readonly language: Language;
  /** How amounts and dates are written. A function of `language` and of nothing else. */
  readonly locale: LanguageLocale;
}

/**
 * The `localStorage` key, exported because `apps/app/index.html` has to spell it out literally — an
 * inline pre-paint script cannot import a module. `language-storage-key.test.ts` asserts the two
 * agree, for the reason `theme-storage-key.test.ts` gives about its own: a silent disagreement looks
 * exactly like a picker that forgets.
 */
export const LANGUAGE_STORAGE_KEY = "gladis-language";

/** The attribute assistive technology reads to know how to pronounce the page. */
export const LANGUAGE_ATTRIBUTE = "lang";

/**
 * Dutch, because the household is Dutch.
 *
 * Every fallback in this module returns it: a browser asking for a language that is not offered, a
 * browser with `localStorage` disabled, a render with no `window`, a `catch`. It is also
 * `money.ts`'s own `DEFAULT_LOCALE`, so the words and the figures agree on the fallback by
 * construction rather than by two people having picked the same one.
 */
const DEFAULT_LANGUAGE: Language = "nl";

const LOCALE_OF: Record<Language, LanguageLocale> = {
  nl: "nl-NL",
  en: "en-GB",
};

const listeners = new Set<() => void>();

/** Narrows an arbitrary string to a language this application offers. */
function asLanguage(value: string | null | undefined): Language | null {
  return value === "nl" || value === "en" ? value : null;
}

/**
 * The choice as this page knows it, which is not always what storage knows.
 *
 * ==========================================================================================
 * WHY MEMORY IS THE SOURCE OF TRUTH AND STORAGE IS A WRITE-THROUGH CACHE
 * ==========================================================================================
 *
 * "A blocked-storage browser gets a control that works and forgets" only holds if the choice
 * survives the rest of the page. Re-reading storage after every write does not give that: the write
 * throws, the read throws, and the reader's choice is discarded a microsecond after they made it —
 * so the control does NOTHING rather than forgetting, which is the opposite of the intent.
 *
 * It also removes a `localStorage` hit from every render. `useSyncExternalStore` calls the snapshot
 * function during render, on every subscribed component.
 *
 * `null` means "not yet read", not "no choice" — `system` is a real value that must be
 * distinguishable from an unread memo, or the first read after an explicit `system` would go back
 * to storage every time.
 */
let choiceMemo: LanguageChoice | null = null;

/**
 * Reads the choice, treating anything unrecognised as `system`.
 *
 * `localStorage` THROWS rather than returning null in a Safari private window and wherever site data
 * is blocked outright. An unhandled throw here would be thrown during render — see the memo above —
 * so it would blank the entire application over a preference.
 */
export function readLanguageChoice(): LanguageChoice {
  if (choiceMemo !== null) return choiceMemo;

  try {
    // Anything that is not one of the two languages is `system`, which is also what "system" itself
    // and an absent key mean — FR-016. There is no fourth state to get wrong.
    choiceMemo = asLanguage(window.localStorage.getItem(LANGUAGE_STORAGE_KEY)) ?? "system";
  } catch {
    choiceMemo = "system";
  }

  return choiceMemo;
}

/** Drops the memo so the next read goes back to storage. For another tab's write, and for tests. */
export function forgetLanguageMemo(): void {
  choiceMemo = null;
}

/**
 * What the browser itself is asking for, reduced to a language this application offers.
 *
 * `navigator.languages` BEFORE `navigator.language`, which is the ordering `main.tsx` already argues
 * for the money locale: the first is the ordered preference list the reader actually configured, the
 * second is a single tag some browsers derive from the UI language rather than the regional format.
 *
 * MATCHED ON THE PRIMARY SUBTAG, so `nl-BE` is Dutch and `en-AU` is English. A reader whose list is
 * `fr, nl, en` gets Dutch — the first entry that can be served, not the first entry outright, and not
 * the fallback.
 */
function browserLanguage(): Language {
  try {
    const tags = [...(window.navigator.languages ?? []), window.navigator.language];
    for (const tag of tags) {
      const offered = asLanguage(tag?.split("-")[0]?.toLowerCase());
      if (offered !== null) return offered;
    }
  } catch {
    /* No navigator, or a hostile one. The household's own language is the answer. */
  }
  return DEFAULT_LANGUAGE;
}

/**
 * The choice with `system` collapsed, plus the formatting locale it implies.
 *
 * Pure, so the control can label a state it is not currently in without applying it.
 */
export function resolveLanguage(choice: LanguageChoice): ResolvedLanguage {
  const language = choice === "system" ? browserLanguage() : choice;
  return { language, locale: LOCALE_OF[language] };
}

/**
 * Puts the resolved language on `<html>` and into the money formatter, and reports what it applied.
 *
 * BOTH, IN ONE FUNCTION, ON PURPOSE. These are the two things outside React that have to agree with
 * the words on screen, and the only way they can drift is if some path updates one and forgets the
 * other. There is no such path, because there is no way to do one of them.
 */
export function applyResolvedLanguage(): ResolvedLanguage {
  const resolved = resolveLanguage(readLanguageChoice());
  document.documentElement.setAttribute(LANGUAGE_ATTRIBUTE, resolved.language);
  setMoneyLocale(resolved.locale);
  return resolved;
}

/**
 * Records a choice and applies it in the same tick.
 *
 * The write is allowed to fail — see `readLanguageChoice` for where it throws — and when it does the
 * language still changes for this page, it simply is not remembered. That is the right way round: a
 * blocked-storage browser gets a control that works and forgets, not one that does nothing.
 */
export function setLanguageChoice(choice: LanguageChoice): void {
  // The memo FIRST, so what follows applies the choice that was made rather than what storage
  // managed to keep. Write-through second, and it is allowed to fail.
  choiceMemo = choice;

  try {
    window.localStorage.setItem(LANGUAGE_STORAGE_KEY, choice);
  } catch {
    /* Storage is unavailable. The application below is what the reader actually asked for. */
  }
  applyResolvedLanguage();
  for (const listener of listeners) listener();
}

/**
 * Re-applies and re-broadcasts when something outside this tab changes the answer.
 *
 * TWO SOURCES, and both are easy to forget. `languagechange` fires when the browser's own preference
 * list changes, which must move a `system` reader immediately — that is the entire meaning of the
 * option. And `storage` fires when ANOTHER tab writes the key, so two open tabs do not sit in
 * opposite languages until one is reloaded.
 */
function onExternalChange(): void {
  // The memo is this page's answer, and one of the two events above means another page changed it.
  // Dropping it is what makes the `storage` half work at all.
  forgetLanguageMemo();
  applyResolvedLanguage();
  for (const listener of listeners) listener();
}

function attach(): void {
  window.addEventListener("languagechange", onExternalChange);
  window.addEventListener("storage", onExternalChange);
}

function detach(): void {
  window.removeEventListener("languagechange", onExternalChange);
  window.removeEventListener("storage", onExternalChange);
}

function subscribe(onChange: () => void): () => void {
  listeners.add(onChange);
  if (listeners.size === 1) attach();
  return () => {
    listeners.delete(onChange);
    if (listeners.size === 0) detach();
  };
}

function choiceSnapshot(): LanguageChoice {
  return readLanguageChoice();
}

/**
 * The RESOLVED LANGUAGE AS A STRING, not as the `ResolvedLanguage` object.
 *
 * `getSnapshot` must be referentially stable between changes or React re-renders forever looking for
 * a fixed point, and a fresh object literal never is. `theme.ts` hit this and documents it; the
 * object is rebuilt in the hook below, where it is an ordinary render value.
 */
function languageSnapshot(): Language {
  return resolveLanguage(readLanguageChoice()).language;
}

function serverChoice(): LanguageChoice {
  return "system";
}

function serverLanguage(): Language {
  return DEFAULT_LANGUAGE;
}

export interface LanguageState {
  /** What the reader picked, including `system`. What the control labels itself with. */
  choice: LanguageChoice;
  /** What that means right now, and how figures are written while it does. */
  resolved: ResolvedLanguage;
  setChoice: (choice: LanguageChoice) => void;
}

/** The language, for any component that needs to know it. */
export function useLanguage(): LanguageState {
  const choice = useSyncExternalStore(subscribe, choiceSnapshot, serverChoice);
  const language = useSyncExternalStore(subscribe, languageSnapshot, serverLanguage);

  return {
    choice,
    resolved: { language, locale: LOCALE_OF[language] },
    setChoice: setLanguageChoice,
  };
}
